import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg, DateSelectArg, EventChangeArg } from '@fullcalendar/core';
import esLocale from '@fullcalendar/core/locales/es';
import { 
    Box, Paper, Typography, Dialog, DialogTitle, DialogContent, 
    DialogActions, Button, TextField, Autocomplete, MenuItem, 
    IconButton, ToggleButton, ToggleButtonGroup, Grid, Alert,
    ListSubheader, Tooltip
} from '@mui/material';
import { 
    Delete as DeleteIcon, 
    ViewModule as ViewModuleIcon, 
    Person as PersonIcon,
    ArrowBack as ArrowBackIcon,
    MeetingRoom as RoomIcon,
    EventBusy as BusyIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// --- INTERFACES ---
interface Patient { id: string; name: string; lastName?: string; cedula?: string; }
interface Doctor { id: string; name: string; lastname?: string; prefix?: string; role: string; }
interface Location { id: string; name: string; type: string; }

// Interfaz para la disponibilidad
interface Availability {
    id: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    doctorId: string;
    locationId: string;
    doctor?: Doctor;
    location?: Location;
}

interface FormState {
    id?: string;
    title: string;
    patientId: string | null;
    doctorId: string;
    locationId: string;
    type: 'CITA' | 'TAREA' | 'CIRUGIA';
    start: string;
    end: string;
    reason: string;
    status: string;
}

const AgendaPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const userRole = user?.role as string;
    const canEditOthers = userRole === 'administrador' || userRole === 'asistente';
    
    // --- ESTADOS ---
    const [events, setEvents] = useState<any[]>([]);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    
    const [viewType, setViewType] = useState<'PERSONAL' | 'GLOBAL'>('PERSONAL');
    const [selectedLocation, setSelectedLocation] = useState<string>('');
    const [filterDoctor, setFilterDoctor] = useState<string>('');

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [form, setForm] = useState<FormState>({
        title: '', patientId: null, doctorId: user?.id || '', locationId: '',
        type: 'CITA', start: '', end: '', reason: '', status: 'pendiente'
    });

    const getStatusColor = (status: string, type: string) => {
        if (type === 'CIRUGIA') return status === 'realizada' ? '#1b5e20' : '#b71c1c'; // Verde oscuro / Rojo oscuro
        if (type === 'TAREA') return '#607d8b'; // Gris azulado
        switch(status) {
            case 'confirmada': return '#2e7d32'; // Verde
            case 'atendida': return '#1976d2';   // Azul
            case 'cancelada': return '#d32f2f';  // Rojo
            default: return '#ed6c02';           // Naranja (Pendiente)
        }
    };

    // --- CARGA DE DATOS ---
    const fetchData = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // 1. Construir filtros para CITAS
            const paramsAppo = new URLSearchParams();
            if (viewType === 'PERSONAL' && !canEditOthers) {
                paramsAppo.append('doctorId', user?.id || '');
            } else if (filterDoctor) {
                paramsAppo.append('doctorId', filterDoctor);
            }
            if (selectedLocation) {
                paramsAppo.append('locationId', selectedLocation);
            }

            // 2. Construir filtros para DISPONIBILIDAD (Horarios)
            const paramsAvail = new URLSearchParams();
            if (filterDoctor) paramsAvail.append('doctorId', filterDoctor);
            // Nota: El endpoint de availability debe soportar filtrar por locationId si se desea, 
            // pero normalmente filtramos por médico.

            const [resAppo, resAvail, resPat, resDoc, resLoc] = await Promise.all([
                fetch(`${API_URL}/api/appointments?${paramsAppo.toString()}`, { headers }),
                fetch(`${API_URL}/api/availability?${paramsAvail.toString()}`, { headers }), // ✅ Traemos horarios
                fetch(`${API_URL}/api/patients`, { headers }),
                fetch(`${API_URL}/api/users`, { headers }),
                fetch(`${API_URL}/api/locations`, { headers })
            ]);

            let finalEvents: any[] = [];

            // A) PROCESAR HORARIOS (Bloques de fondo)
            if (resAvail.ok) {
                const availabilities: Availability[] = await resAvail.json();
                
                const backgroundEvents = availabilities
                    .filter(av => {
                        // Si hay filtro de ubicación, solo mostrar horarios de esa ubicación
                        if (selectedLocation && av.locationId !== selectedLocation) return false;
                        // Si es vista personal, solo mis horarios
                        if (viewType === 'PERSONAL' && !canEditOthers && av.doctorId !== user?.id) return false;
                        return true;
                    })
                    .map(av => ({
                        // FullCalendar Recurrent Event
                        groupId: 'availableForBooking',
                        daysOfWeek: [av.dayOfWeek], // 0=Dom, 1=Lun, etc.
                        startTime: av.startTime,
                        endTime: av.endTime,
                        display: 'background', // ✅ Esto lo pone sombreado al fondo
                        color: '#e8f5e9', // Verde muy claro (Disponibilidad)
                        // Título para tooltip (FullCalendar a veces no muestra título en background, pero sirve de ref)
                        title: `Horario: ${av.doctor?.name} (${av.location?.name})`, 
                        extendedProps: { isAvailability: true, ...av }
                    }));
                
                finalEvents = [...finalEvents, ...backgroundEvents];
            }

            // B) PROCESAR CITAS (Bloques sólidos)
            if (resAppo.ok) {
                const data = await resAppo.json();
                const appointmentEvents = data.map((appo: any) => {
                    const isMine = appo.doctorId === user?.id;
                    const isOccupiedLocation = selectedLocation && !isMine;

                    return {
                        id: appo.id,
                        title: appo.type === 'CITA' 
                            ? `${appo.patient?.name || 'S/N'} (${appo.doctor?.lastname || ''})`
                            : `${appo.title} - ${appo.doctor?.lastname || ''}`,
                        start: appo.start,
                        end: appo.end,
                        backgroundColor: getStatusColor(appo.status, appo.type),
                        borderColor: 'transparent',
                        classNames: isOccupiedLocation ? ['occupied-event'] : [], 
                        opacity: isOccupiedLocation ? 0.6 : 1, 
                        editable: isMine || canEditOthers,
                        extendedProps: { isAvailability: false, ...appo }
                    };
                });
                finalEvents = [...finalEvents, ...appointmentEvents];
            }

            setEvents(finalEvents);

            if (resPat.ok) setPatients(await resPat.json());
            if (resDoc.ok) {
                const users: Doctor[] = await resDoc.json();
                setDoctors(users.filter(u => u.role === 'medico' || u.role === 'administrador'));
            }
            if (resLoc.ok) setLocations(await resLoc.json());

        } catch (e) { console.error(e); }
    }, [user, viewType, selectedLocation, filterDoctor, canEditOthers]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // --- MANEJADORES ---
    const handleSave = async () => {
        setErrorMsg(null);
        if (form.type === 'CITA' && !form.patientId) return setErrorMsg("Seleccione un paciente.");
        if (form.type !== 'TAREA' && !form.locationId) return setErrorMsg("Seleccione una ubicación.");
        if (!form.doctorId) return setErrorMsg("Asigne un médico.");

        const method = selectedEvent ? 'PUT' : 'POST';
        const url = selectedEvent ? `${API_URL}/api/appointments/${form.id}` : `${API_URL}/api/appointments`;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ ...form })
            });

            const data = await res.json();

            if (!res.ok) {
                setErrorMsg(data.error || "Error al guardar");
            } else {
                setModalOpen(false);
                fetchData();
            }
        } catch (e) { setErrorMsg("Error de conexión"); }
    };

    const handleDelete = async () => {
        if (!window.confirm("¿Eliminar registro?")) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/appointments/${form.id}`, { 
                method: 'DELETE', 
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setModalOpen(false);
            fetchData();
        } catch (e) { alert("Error al eliminar"); }
    };

    const handleDateSelect = (info: DateSelectArg) => {
        setForm({
            title: '', patientId: null, doctorId: user?.id || '', locationId: selectedLocation,
            type: 'CITA', start: info.startStr, end: info.endStr, reason: '', status: 'pendiente'
        });
        setSelectedEvent(null);
        setErrorMsg(null);
        setModalOpen(true);
    };

    const handleEventClick = (info: EventClickArg) => {
        // Ignorar clics en los eventos de fondo (disponibilidad)
        if (info.event.display === 'background') return;

        const props = info.event.extendedProps;
        setSelectedEvent(info.event);
        setForm({
            id: props.id, title: props.title || '', patientId: props.patientId,
            doctorId: props.doctorId, locationId: props.locationId || '',
            type: props.type || 'CITA', start: info.event.startStr, end: info.event.endStr,
            reason: props.reason || '', status: props.status
        });
        setErrorMsg(null);
        setModalOpen(true);
    };

    const groupedLocations = locations.reduce((acc: any, loc) => {
        const type = loc.type || 'General';
        if (!acc[type]) acc[type] = [];
        acc[type].push(loc);
        return acc;
    }, {});

    return (
        <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
            
            {/* --- BARRA SUPERIOR --- */}
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} variant="outlined" color="inherit">
                    Volver
                </Button>

                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                        {viewType === 'PERSONAL' ? 'Mi Agenda Médica' : 'Gestión de Espacios y Recursos'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {selectedLocation 
                            ? `Filtrado por: ${locations.find(l => l.id === selectedLocation)?.name}` 
                            : 'Vista General'}
                    </Typography>
                </Box>

                <ToggleButtonGroup
                    value={viewType}
                    exclusive
                    onChange={(_, v) => { if(v) setViewType(v); }}
                    size="small"
                >
                    <ToggleButton value="PERSONAL"><PersonIcon sx={{ mr: 1 }} /> Mis Citas</ToggleButton>
                    <ToggleButton value="GLOBAL"><ViewModuleIcon sx={{ mr: 1 }} /> Recursos</ToggleButton>
                </ToggleButtonGroup>

                <TextField
                    select
                    label="Filtrar por Espacio"
                    size="small"
                    sx={{ width: 220 }}
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    InputProps={{ startAdornment: <RoomIcon color="action" sx={{ mr: 1 }} /> }}
                >
                    <MenuItem value=""><em>-- Todas las Ubicaciones --</em></MenuItem>
                    {Object.keys(groupedLocations).map((type) => [
                        <ListSubheader key={`header-${type}`} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {type}
                        </ListSubheader>,
                        ...groupedLocations[type].map((loc: Location) => (
                            <MenuItem key={loc.id} value={loc.id} sx={{ pl: 4 }}>
                                {loc.name}
                            </MenuItem>
                        ))
                    ])}
                </TextField>

                {canEditOthers && viewType === 'GLOBAL' && (
                    <TextField
                        select
                        label="Filtrar Médico"
                        size="small"
                        sx={{ width: 200 }}
                        value={filterDoctor}
                        onChange={(e) => setFilterDoctor(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {doctors.map(d => (
                            <MenuItem key={d.id} value={d.id}>{d.name} {d.lastname}</MenuItem>
                        ))}
                    </TextField>
                )}
            </Paper>

            {/* --- CALENDARIO --- */}
            <Paper elevation={3} sx={{ flexGrow: 1, p: 1, borderRadius: 2, overflow: 'hidden' }}>
                <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    locale={esLocale}
                    editable={true}
                    selectable={true}
                    selectMirror={true}
                    dayMaxEvents={true}
                    events={events}
                    select={handleDateSelect}
                    eventClick={handleEventClick}
                    height="100%"
                    slotMinTime="06:00:00"
                    slotMaxTime="22:00:00"
                    allDaySlot={false}
                    expandRows={true}
                    stickyHeaderDates={true}
                    nowIndicator={true} // Muestra la línea roja de "ahora"
                    businessHours={{
                        // Esto define el horario "visual" global (gris fuera de horario)
                        daysOfWeek: [ 1, 2, 3, 4, 5, 6 ], 
                        startTime: '07:00', 
                        endTime: '19:00', 
                    }}
                />
            </Paper>

            {/* --- MODAL --- */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: form.type === 'CIRUGIA' ? '#b71c1c' : 'primary.main', color: 'white' }}>
                    {selectedEvent ? 'Detalles del Evento' : 'Agendar Nueva Actividad'}
                </DialogTitle>
                <DialogContent dividers>
                    {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    select label="Tipo Actividad" fullWidth size="small"
                                    value={form.type}
                                    onChange={(e) => setForm({ ...form, type: e.target.value as any })}
                                >
                                    <MenuItem value="CITA">Cita Médica</MenuItem>
                                    <MenuItem value="CIRUGIA">Cirugía / Procedimiento</MenuItem>
                                    <MenuItem value="TAREA">Bloqueo / Mantenimiento</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    select label="Estado" fullWidth size="small"
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                >
                                    <MenuItem value="pendiente">Pendiente</MenuItem>
                                    <MenuItem value="confirmada">Confirmada</MenuItem>
                                    <MenuItem value="atendida">Atendida</MenuItem>
                                    <MenuItem value="cancelada" sx={{ color: 'error.main' }}>Cancelada</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>

                        <TextField
                            select label="Ubicación / Sala" fullWidth required={form.type !== 'TAREA'}
                            value={form.locationId}
                            onChange={(e) => setForm({ ...form, locationId: e.target.value })}
                        >
                            {Object.keys(groupedLocations).map((type) => [
                                <ListSubheader key={type} sx={{ fontWeight: 'bold' }}>{type}</ListSubheader>,
                                ...groupedLocations[type].map((loc: Location) => (
                                    <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                                ))
                            ])}
                        </TextField>

                        {form.type === 'CITA' && (
                            <Autocomplete
                                options={patients}
                                getOptionLabel={(o) => `${o.name} ${o.lastName || ''} (${o.cedula || 'S/ID'})`}
                                value={patients.find(p => p.id === form.patientId) || null}
                                onChange={(_, v) => setForm({ ...form, patientId: v?.id || null })}
                                renderInput={(params) => <TextField {...params} label="Paciente" required />}
                            />
                        )}

                        <TextField
                            select label="Médico Responsable" fullWidth
                            value={form.doctorId}
                            onChange={(e) => setForm({ ...form, doctorId: e.target.value })}
                            disabled={!canEditOthers && userRole === 'medico'} 
                        >
                            {doctors.map(d => (
                                <MenuItem key={d.id} value={d.id}>{d.name} {d.lastname}</MenuItem>
                            ))}
                        </TextField>

                        <TextField label="Motivo" fullWidth value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        <TextField label="Notas" fullWidth multiline rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {selectedEvent && <Button startIcon={<DeleteIcon />} color="error" onClick={handleDelete} sx={{ mr: 'auto' }}>Eliminar</Button>}
                    <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>Guardar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AgendaPage;